/**
 * Phân tích SEO on-page — bản đầy đủ, dùng chung `apps/admin` (chấm điểm khi soạn) và
 * `apps/web` (nếu cần kiểm tra lúc build).
 *
 * Cách nghĩ: đây là checklist của **người làm SEO**, không phải "cho có". Mỗi mục
 * (`SeoCheck`) có `weight` theo mức ảnh hưởng thật tới thứ hạng/CTR:
 *   - `3` = ảnh hưởng trực tiếp (từ khoá trong title/H1/slug, độ dài nội dung, indexable)
 *   - `2` = ảnh hưởng rõ (meta description, heading, internal link, alt ảnh)
 *   - `1` = tinh chỉnh (CTA trong description, stop word trong slug, outbound link)
 *
 * Logic thuần, không phụ thuộc React/DOM (parse HTML bằng regex, chạy được cả ở Node).
 */

import { normalizeVi } from './format'

// ─────────────────────────────────────────────────────────────────────────────
// Ngưỡng — một chỗ duy nhất, UI đọc lại để hiện gợi ý cho khớp điểm
// ─────────────────────────────────────────────────────────────────────────────

export const SEO_LIMITS = {
  /** Google cắt title theo **pixel**, không theo ký tự. ~580px ở desktop (Arial 20px). */
  titlePixels: { min: 200, max: 580 },
  /** Description ~920px (Arial 14px) ≈ 155–160 ký tự. */
  descPixels: { min: 430, max: 920 },
  descChars: { min: 120, max: 160 },
  slugChars: 60,
  /**
   * Tiếng Việt mỗi âm tiết là một "từ" trong slug ("moc-khoa-in-3d-ca-nhan-hoa" = 7),
   * nên ngưỡng phải cao hơn tiếng Anh — 6 từ là mức của Yoast cho tiếng Anh, áp vào
   * tiếng Việt thì slug nào cũng bị báo dài.
   */
  slugWords: 10,
  /** Mật độ từ khoá: dưới thì Google không rõ chủ đề, trên thì bị coi là nhồi. */
  density: { min: 0.5, max: 2.5 },
  /**
   * Số từ nội dung tối thiểu — mỗi loại trang một mức, chênh nhau rất nhiều.
   * Danh mục tính **cả** `description` (đoạn giới thiệu) lẫn `longContent` (khối SEO dưới
   * lưới sản phẩm) — Google đọc chúng trên cùng một trang.
   *
   * Ngưỡng cũ `{40, 90}` suy ra từ trần 1000 ký tự của ô mô tả text thuần; từ 2026-09-09 cả
   * hai ô đều là trình soạn thảo nên trần đó không còn. Vẫn thấp hơn bài viết vì trang danh
   * mục chủ yếu là lưới sản phẩm, phần chữ chỉ để bổ nghĩa.
   */
  words: {
    product: { warn: 120, good: 250 },
    post: { warn: 400, good: 800 },
    category: { warn: 100, good: 300 },
  },
  /** Ảnh sản phẩm: 3 ảnh là mức Google Merchant/rich result nhìn đủ tin. */
  productImages: 3,
  longSentenceWords: 30,
  longParagraphWords: 120,
} as const

/** Từ ngữ kêu gọi hành động — description có CTA thì CTR cao hơn rõ rệt. */
const CTA_WORDS = [
  'mua', 'dat hang', 'dat truoc', 'xem', 'kham pha', 'lien he', 'tim hieu', 'nhan',
  'tang', 'mien phi', 'giao hang', 'uu dai', 'giam gia', 'ngay', 'tai day', 'huong dan',
]

/** Stop word tiếng Việt — trong slug là rác, không thêm giá trị tìm kiếm. */
const SLUG_STOP_WORDS = [
  'va', 'cua', 'cho', 'voi', 'la', 'cac', 'nhung', 'mot', 'tai', 'tu', 'den', 'trong',
  'khong', 'de', 'ma', 'thi', 'nay', 'do', 'ra', 've', 'khi', 'boi', 'nen', 'se',
]

// ─────────────────────────────────────────────────────────────────────────────
// Đo chiều rộng chữ (xấp xỉ SERP)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Bảng chiều rộng ký tự theo `em` (Arial) — đủ để biết title/description **có bị cắt**
 * trên Google hay không. Đếm ký tự không làm được việc này: "IIIIIIIIII" và "mmmmmmmmmm"
 * cùng 10 ký tự nhưng rộng gấp ba lần nhau.
 */
function charWidth(ch: string): number {
  if (' \t'.includes(ch)) return 0.28
  if ('ilj|!.,;:\'`'.includes(ch)) return 0.26
  if ('ftIr()[]{}/-'.includes(ch)) return 0.34
  if ('mwMW'.includes(ch)) return 0.87
  if ('0123456789'.includes(ch)) return 0.56
  const code = ch.codePointAt(0) ?? 0
  // Chữ hoa (kể cả có dấu) rộng hơn chữ thường.
  if (ch === ch.toUpperCase() && ch !== ch.toLowerCase()) return 0.68
  if (code > 0x2000) return 0.6
  return 0.55
}

/** Chiều rộng chuỗi ở `fontSize` px, xấp xỉ Arial. */
export function textPixels(text: string, fontSize: number): number {
  let w = 0
  for (const ch of text) w += charWidth(ch) * fontSize
  return Math.round(w)
}

/** Cắt chuỗi như SERP: thêm `…` khi vượt `maxPixels`. */
export function serpTruncate(text: string, maxPixels: number, fontSize: number): { text: string; truncated: boolean } {
  if (textPixels(text, fontSize) <= maxPixels) return { text, truncated: false }
  const ellipsis = textPixels('…', fontSize)
  let out = ''
  let w = 0
  for (const ch of text) {
    const cw = charWidth(ch) * fontSize
    if (w + cw + ellipsis > maxPixels) break
    out += ch
    w += cw
  }
  return { text: `${out.trimEnd()}…`, truncated: true }
}

// ─────────────────────────────────────────────────────────────────────────────
// Parse nội dung HTML (Tiptap)
// ─────────────────────────────────────────────────────────────────────────────

export interface ContentHeading { level: number; text: string }
export interface ContentImage { alt: string; src: string }
export interface ContentLink { href: string; text: string; internal: boolean }

const decodeEntities = (s: string) =>
  s.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")

/** HTML → text thuần. Chèn khoảng trắng sau tag để không dính từ ("3D" + "shop"). */
export function htmlToText(html: string | null | undefined): string {
  if (!html) return ''
  return decodeEntities(
    html
      .replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, '\n')
      .replace(/<[^>]+>/g, ' '),
  )
    .replace(/[ \t]+/g, ' ')
    .replace(/\n\s*\n+/g, '\n\n')
    .trim()
}

export function extractHeadings(html: string | null | undefined): ContentHeading[] {
  if (!html) return []
  const out: ContentHeading[] = []
  const re = /<h([1-6])[^>]*>([\s\S]*?)<\/h\1>/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(html))) {
    out.push({ level: Number(m[1]), text: htmlToText(m[2]) })
  }
  return out
}

export function extractImages(html: string | null | undefined): ContentImage[] {
  if (!html) return []
  const out: ContentImage[] = []
  const re = /<img\b[^>]*>/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(html))) {
    const tag = m[0]
    const alt = /\balt\s*=\s*"([^"]*)"/i.exec(tag)?.[1] ?? /\balt\s*=\s*'([^']*)'/i.exec(tag)?.[1] ?? ''
    const src = /\bsrc\s*=\s*"([^"]*)"/i.exec(tag)?.[1] ?? ''
    out.push({ alt: decodeEntities(alt).trim(), src })
  }
  return out
}

export function extractLinks(html: string | null | undefined, siteHost = ''): ContentLink[] {
  if (!html) return []
  const out: ContentLink[] = []
  const re = /<a\b[^>]*href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(html))) {
    const href = m[1]!.trim()
    if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) continue
    const isAbsolute = /^https?:\/\//i.test(href)
    const internal = !isAbsolute || (!!siteHost && href.toLowerCase().includes(siteHost.toLowerCase()))
    out.push({ href, text: htmlToText(m[2]), internal })
  }
  return out
}

// ─────────────────────────────────────────────────────────────────────────────
// Thống kê văn bản
// ─────────────────────────────────────────────────────────────────────────────

function words(text: string): string[] {
  const t = text.trim()
  return t ? t.split(/\s+/) : []
}

/** Số lần từ khoá xuất hiện (không phân biệt dấu/hoa-thường). */
export function countKeyword(text: string, keyword: string): number {
  const k = normalizeVi(keyword).trim()
  if (!k) return 0
  const hay = normalizeVi(text)
  let n = 0
  let i = hay.indexOf(k)
  while (i !== -1) {
    n += 1
    i = hay.indexOf(k, i + k.length)
  }
  return n
}

export type KeywordMatch = 'exact' | 'partial' | 'none'

/**
 * Mức khớp từ khoá trong một đoạn text.
 *
 * `exact` = có đúng cụm liền. `partial` = **mọi từ** trong từ khoá đều xuất hiện nhưng
 * không liền nhau — vd từ khoá "móc khoá in 3d cá nhân hoá" trong tiêu đề "Móc khoá
 * **cáo** in 3D cá nhân hoá". Đây là trường hợp rất thường gặp và hoàn toàn ổn với
 * Google (nó hiểu biến thể), nên chấm `warn` chứ không phải `bad` — coi là "không có"
 * sẽ khiến người dùng nhồi cụm liền vào tiêu đề một cách kỳ quặc.
 */
export function matchKeyword(text: string | null | undefined, keyword: string): KeywordMatch {
  if (!keyword) return 'none'
  const hay = normalizeVi(text || '')
  const k = normalizeVi(keyword).trim()
  if (!k) return 'none'
  if (hay.includes(k)) return 'exact'
  const parts = k.split(/\s+/).filter(Boolean)
  return parts.length > 1 && parts.every((w) => hay.includes(w)) ? 'partial' : 'none'
}

/** `exact` → good, `partial` → warn, `none` → bad. */
const matchStatus = (m: KeywordMatch): CheckStatus => (m === 'exact' ? 'good' : m === 'partial' ? 'warn' : 'bad')

/** Câu giải thích thêm khi chỉ khớp một phần. */
const PARTIAL_NOTE = ' Hiện các từ có xuất hiện nhưng không thành cụm liền — Google vẫn hiểu, cụm liền thì mạnh hơn một chút.'

// ─────────────────────────────────────────────────────────────────────────────
// Kiểu dữ liệu
// ─────────────────────────────────────────────────────────────────────────────

export type CheckStatus = 'good' | 'warn' | 'bad'
export type SeoGroup = 'keyword' | 'serp' | 'slug' | 'content' | 'links' | 'media' | 'readability' | 'technical' | 'rich'

export const SEO_GROUP_LABEL: Record<SeoGroup, string> = {
  keyword: 'Từ khoá',
  serp: 'Tiêu đề & mô tả trên Google',
  slug: 'Đường dẫn',
  content: 'Nội dung',
  links: 'Liên kết',
  media: 'Hình ảnh',
  readability: 'Dễ đọc',
  technical: 'Kỹ thuật',
  rich: 'Kết quả nổi bật (rich result)',
}

export interface SeoCheck {
  id: string
  group: SeoGroup
  label: string
  status: CheckStatus
  hint: string
  weight: 1 | 2 | 3
}

export interface SeoStats {
  titleChars: number
  titlePixels: number
  descChars: number
  descPixels: number
  words: number
  readingMinutes: number
  keywordCount: number
  keywordDensity: number
  headings: ContentHeading[]
  internalLinks: number
  externalLinks: number
  contentImages: number
  imagesMissingAlt: number
  sentences: number
  longSentences: number
  avgSentenceWords: number
  longParagraphs: number
}

export interface SeoResult {
  score: number
  rating: 'good' | 'ok' | 'bad'
  checks: SeoCheck[]
  /** Các mục chưa đạt, sắp theo mức ảnh hưởng giảm dần — "làm gì tiếp theo". */
  todos: SeoCheck[]
  stats: SeoStats
}

export interface SeoInput {
  /**
   * ⚠️ Thêm giá trị mới ở đây **bắt buộc** phải thêm khoá tương ứng trong
   * `SEO_LIMITS.words` — thiếu là `SEO_LIMITS.words[kind]` ném `TypeError` lúc chạy,
   * không phải lúc build, và chỉ nổ khi có người mở đúng tab SEO của loại đó.
   */
  kind?: 'product' | 'post' | 'category'
  focusKeyword?: string | null
  /** Từ khoá phụ (long-tail) — nên xuất hiện trong nội dung. */
  secondaryKeywords?: string[]
  metaTitle?: string | null
  metaDescription?: string | null
  hasMetaImage?: boolean
  slug?: string | null
  /** Tiêu đề hiển thị (H1) của sản phẩm/bài viết. */
  productTitle?: string | null
  /** HTML nội dung (Tiptap). Ưu tiên dùng cái này. */
  html?: string | null
  /** Text thuần — chỉ dùng khi không có `html`. */
  description?: string | null
  /** Ảnh gắn ngoài nội dung: ảnh sản phẩm / ảnh đại diện bài viết. */
  attachedImages?: { alt: string }[]
  siteHost?: string

  // Sản phẩm
  price?: number | null
  hasSku?: boolean
  hasCategory?: boolean
  brand?: string | null
  hasVideo?: boolean
  faqCount?: number

  // Bài viết
  hasExcerpt?: boolean
  hasAuthor?: boolean
  hasPublishedAt?: boolean
  tagCount?: number

  // Danh mục
  /** Số sản phẩm thuộc danh mục (tính cả nhánh con). */
  productCount?: number
  /** Số danh mục con trực tiếp. */
  childCount?: number

  // Kỹ thuật
  noindex?: boolean
  canonical?: string | null

  /** Trùng lặp do server phát hiện (`GET /api/seo/conflicts`). */
  duplicateTitleWith?: string | null
  duplicateKeywordWith?: string | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Phân tích
// ─────────────────────────────────────────────────────────────────────────────

export function analyzeSeo(input: SeoInput): SeoResult {
  const kind = input.kind ?? 'product'
  const kw = (input.focusKeyword ?? '').trim()
  const hasKw = kw.length > 0
  const secondary = (input.secondaryKeywords ?? []).map((s) => s.trim()).filter(Boolean)
  const h1 = input.productTitle ?? ''
  const title = (input.metaTitle || h1 || '').trim()
  const desc = (input.metaDescription ?? '').trim()
  const slug = (input.slug ?? '').trim()
  const html = input.html ?? null
  const text = html ? htmlToText(html) : (input.description ?? '')

  const headings = extractHeadings(html)
  const contentImages = extractImages(html)
  const links = extractLinks(html, input.siteHost)
  const attached = input.attachedImages ?? []
  const allImages = [...attached, ...contentImages]

  const wordList = words(text)
  const wordCount = wordList.length
  const kwCount = hasKw ? countKeyword(text, kw) : 0
  const density = hasKw && wordCount ? (kwCount * words(kw).length * 100) / wordCount : 0

  const paragraphs = text.split(/\n{2,}|\n/).map((p) => p.trim()).filter(Boolean)
  const sentences = text.split(/[.!?…]+\s|\n+/).map((s) => s.trim()).filter((s) => s.length > 1)
  const sentenceWordCounts = sentences.map((s) => words(s).length)
  const longSentences = sentenceWordCounts.filter((n) => n > SEO_LIMITS.longSentenceWords).length
  const longParagraphs = paragraphs.filter((p) => words(p).length > SEO_LIMITS.longParagraphWords).length
  const avgSentenceWords = sentences.length ? Math.round(wordCount / sentences.length) : 0

  const firstParagraph = paragraphs[0] ?? wordList.slice(0, 100).join(' ')

  const stats: SeoStats = {
    titleChars: title.length,
    titlePixels: textPixels(title, 20),
    descChars: desc.length,
    descPixels: textPixels(desc, 14),
    words: wordCount,
    readingMinutes: Math.max(1, Math.round(wordCount / 200)),
    keywordCount: kwCount,
    keywordDensity: Math.round(density * 100) / 100,
    headings,
    internalLinks: links.filter((l) => l.internal).length,
    externalLinks: links.filter((l) => !l.internal).length,
    contentImages: contentImages.length,
    imagesMissingAlt: allImages.filter((i) => !i.alt.trim()).length,
    sentences: sentences.length,
    longSentences,
    avgSentenceWords,
    longParagraphs,
  }

  const checks: SeoCheck[] = []
  const add = (c: SeoCheck) => checks.push(c)

  // ── Từ khoá ────────────────────────────────────────────────────────────────
  add({
    id: 'focus-keyword', group: 'keyword', weight: 3,
    label: hasKw ? `Từ khoá chính: "${kw}"` : 'Chưa đặt từ khoá chính',
    status: hasKw ? 'good' : 'bad',
    hint: 'Từ khoá chính là cụm người mua thật sự gõ vào Google. Cụ thể hơn thì dễ lên top hơn: "móc khoá in 3d cá nhân hoá" tốt hơn "móc khoá".',
  })
  if (hasKw) {
    const kwWords = words(kw).length
    add({
      id: 'keyword-specific', group: 'keyword', weight: 1,
      label: `Độ cụ thể của từ khoá (${kwWords} từ)`,
      status: kwWords >= 3 ? 'good' : kwWords === 2 ? 'warn' : 'bad',
      hint: 'Từ khoá 3–5 từ (long-tail) ít cạnh tranh, đúng ý định mua hơn. Một từ đơn gần như không thể lên top với shop nhỏ.',
    })
    const h1Match = matchKeyword(h1, kw)
    add({
      id: 'kw-in-h1', group: 'keyword', weight: 3,
      label: 'Từ khoá trong tiêu đề trang (H1)',
      status: matchStatus(h1Match),
      hint: 'H1 là tín hiệu chủ đề mạnh nhất trên trang. Đưa từ khoá vào tên sản phẩm/bài viết một cách tự nhiên.' + (h1Match === 'partial' ? PARTIAL_NOTE : ''),
    })
    if (secondary.length) {
      const found = secondary.filter((s) => matchKeyword(text, s) !== 'none').length
      add({
        id: 'secondary-keywords', group: 'keyword', weight: 1,
        label: `Từ khoá phụ trong nội dung (${found}/${secondary.length})`,
        status: found === secondary.length ? 'good' : found > 0 ? 'warn' : 'bad',
        hint: 'Từ khoá phụ giúp trang bắt được nhiều truy vấn dài khác nhau mà không cần viết thêm bài.',
      })
    }
    if (input.duplicateKeywordWith) {
      add({
        id: 'keyword-cannibalization', group: 'keyword', weight: 2,
        label: `Từ khoá đang bị dùng trùng với "${input.duplicateKeywordWith}"`,
        status: 'bad',
        hint: 'Hai trang cùng nhắm một từ khoá sẽ tự cạnh tranh nhau (keyword cannibalization) và cả hai đều tụt. Đổi một bên sang từ khoá khác hoặc gộp nội dung.',
      })
    }
  }

  // ── Title & description (SERP) ─────────────────────────────────────────────
  if (hasKw) {
    const titleMatch = matchKeyword(title, kw)
    const pos = normalizeVi(title).indexOf(normalizeVi(kw).trim())
    add({
      id: 'kw-in-title', group: 'serp', weight: 3,
      label: 'Từ khoá trong tiêu đề SEO',
      status: matchStatus(titleMatch),
      hint: 'Tiêu đề SEO là dòng xanh trên Google. Không có từ khoá thì gần như không xếp hạng cho từ khoá đó.' + (titleMatch === 'partial' ? PARTIAL_NOTE : ''),
    })
    if (pos >= 0) {
      add({
        id: 'kw-title-start', group: 'serp', weight: 1,
        label: 'Từ khoá nằm ở nửa đầu tiêu đề',
        status: pos <= Math.max(1, title.length / 2) ? 'good' : 'warn',
        hint: 'Từ khoá càng gần đầu tiêu đề càng có trọng số cao và người dùng đọc thấy ngay.',
      })
    }
  }
  const tw = stats.titlePixels
  add({
    id: 'title-width', group: 'serp', weight: 3,
    label: `Độ dài tiêu đề (${stats.titleChars} ký tự ≈ ${tw}px)`,
    status: tw >= SEO_LIMITS.titlePixels.min && tw <= SEO_LIMITS.titlePixels.max ? 'good'
      : tw > 0 && tw <= SEO_LIMITS.titlePixels.max * 1.12 ? 'warn' : 'bad',
    hint: `Google cắt tiêu đề theo pixel (~${SEO_LIMITS.titlePixels.max}px), không theo số ký tự. Quá ngắn thì mất cơ hội chứa từ khoá; quá dài thì bị cắt bằng "…".`,
  })
  if (input.duplicateTitleWith) {
    add({
      id: 'title-duplicate', group: 'serp', weight: 2,
      label: `Tiêu đề SEO trùng với "${input.duplicateTitleWith}"`,
      status: 'bad',
      hint: 'Tiêu đề trùng làm Google không biết chọn trang nào, và trong Search Console báo lỗi "Duplicate title".',
    })
  }
  add({
    id: 'desc-set', group: 'serp', weight: 2,
    label: desc ? `Độ dài mô tả (${stats.descChars} ký tự ≈ ${stats.descPixels}px)` : 'Chưa có meta description',
    status: !desc ? 'bad'
      : stats.descPixels >= SEO_LIMITS.descPixels.min && stats.descPixels <= SEO_LIMITS.descPixels.max ? 'good' : 'warn',
    hint: `Mô tả không xếp hạng trực tiếp nhưng quyết định người ta có bấm vào hay không. Nhắm ${SEO_LIMITS.descChars.min}–${SEO_LIMITS.descChars.max} ký tự; để trống thì Google tự cắt một đoạn bất kỳ trong bài.`,
  })
  if (hasKw && desc) {
    const m = matchKeyword(desc, kw)
    add({
      id: 'kw-in-desc', group: 'serp', weight: 2,
      label: 'Từ khoá trong mô tả',
      status: m === 'none' ? 'bad' : m === 'exact' ? 'good' : 'warn',
      hint: 'Google in đậm từ khoá trùng truy vấn trong mô tả — đậm thì mắt người dùng dừng lại.' + (m === 'partial' ? PARTIAL_NOTE : ''),
    })
  }
  if (desc) {
    add({
      id: 'desc-cta', group: 'serp', weight: 1,
      label: 'Mô tả có lời kêu gọi hành động',
      status: CTA_WORDS.some((w) => normalizeVi(desc).includes(w)) ? 'good' : 'warn',
      hint: 'Thêm động từ hành động ("Mua ngay", "Xem mẫu", "Đặt riêng theo tên") giúp tăng tỉ lệ bấm.',
    })
  }

  // ── Slug ───────────────────────────────────────────────────────────────────
  add({
    id: 'slug-format', group: 'slug', weight: 2,
    label: 'Slug hợp lệ (chữ thường, gạch nối)',
    status: !!slug && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ? 'good' : 'bad',
    hint: 'Chỉ a–z, 0–9 và gạch nối. Không dấu, không khoảng trắng, không chữ hoa — URL có ký tự lạ bị encode thành %C3%A1 rất khó chia sẻ.',
  })
  if (hasKw) {
    const m = matchKeyword(slug.replace(/-/g, ' '), kw)
    add({
      id: 'kw-in-slug', group: 'slug', weight: 3,
      label: 'Từ khoá trong slug',
      status: matchStatus(m),
      hint: 'Slug chứa từ khoá được in đậm trên SERP và là tín hiệu xếp hạng ổn định. Ví dụ: /san-pham/moc-khoa-in-3d-ca-nhan-hoa.' + (m === 'partial' ? PARTIAL_NOTE : ''),
    })
  }
  if (slug) {
    const slugWordList = slug.split('-').filter(Boolean)
    add({
      id: 'slug-length', group: 'slug', weight: 1,
      label: `Độ dài slug (${slugWordList.length} từ, ${slug.length} ký tự)`,
      status: slugWordList.length <= SEO_LIMITS.slugWords && slug.length <= SEO_LIMITS.slugChars ? 'good' : 'warn',
      hint: `Slug ngắn dễ nhớ, dễ chia sẻ. Nhắm ≤ ${SEO_LIMITS.slugWords} từ và ≤ ${SEO_LIMITS.slugChars} ký tự.`,
    })
    const stops = slugWordList.filter((w) => SLUG_STOP_WORDS.includes(w))
    add({
      id: 'slug-stopwords', group: 'slug', weight: 1,
      label: stops.length ? `Slug có từ nối vô nghĩa: ${stops.join(', ')}` : 'Slug không có từ nối vô nghĩa',
      status: stops.length ? 'warn' : 'good',
      hint: 'Bỏ "va", "cua", "cho", "voi"… khỏi slug: chúng chiếm chỗ mà không mang ý nghĩa tìm kiếm.',
    })
  }

  // ── Nội dung ───────────────────────────────────────────────────────────────
  const wl = SEO_LIMITS.words[kind]
  add({
    id: 'content-length', group: 'content', weight: 3,
    label: `Độ dài nội dung (${wordCount} từ ≈ ${stats.readingMinutes} phút đọc)`,
    status: wordCount >= wl.good ? 'good' : wordCount >= wl.warn ? 'warn' : 'bad',
    hint: kind === 'product'
      ? `Mô tả sản phẩm nên ≥ ${wl.good} từ: chất liệu, kích thước, cách dùng, cách bảo quản, ai nên mua. Trang mỏng vài chục từ rất khó cạnh tranh với Shopee.`
      : kind === 'category'
        ? `Tính cả mô tả đầu trang và khối nội dung dài — nên ≥ ${wl.good} từ: nhóm này gồm những gì, hợp với ai, chọn thế nào, khác gì các danh mục kia. Trang chỉ có lưới ảnh thì Google không có chữ nào để hiểu chủ đề.`
        : `Bài viết nên ≥ ${wl.good} từ để đủ sâu cho một chủ đề. Dưới ${wl.warn} từ thường bị xem là nội dung mỏng.`,
  })
  if (hasKw) {
    const fpMatch = matchKeyword(firstParagraph, kw)
    add({
      id: 'kw-in-first-paragraph', group: 'content', weight: 3,
      label: 'Từ khoá trong đoạn mở đầu',
      status: matchStatus(fpMatch),
      hint: 'Nhắc từ khoá trong ~100 từ đầu để Google (và người đọc) xác định chủ đề ngay lập tức.' + (fpMatch === 'partial' ? PARTIAL_NOTE : ''),
    })
    const bodyMatch = matchKeyword(text, kw)
    add({
      id: 'kw-density', group: 'content', weight: 2,
      label: `Mật độ từ khoá ${stats.keywordDensity}% (cụm liền xuất hiện ${kwCount} lần)`,
      status: wordCount < 40 ? 'warn'
        : stats.keywordDensity >= SEO_LIMITS.density.min && stats.keywordDensity <= SEO_LIMITS.density.max ? 'good'
        : bodyMatch === 'none' ? 'bad' : 'warn',
      hint: kwCount === 0 && bodyMatch === 'partial'
        ? `Nội dung có đủ các từ nhưng chưa lần nào thành cụm liền "${kw}". Viết nguyên cụm 1–2 lần một cách tự nhiên.`
        : `Nhắm ${SEO_LIMITS.density.min}–${SEO_LIMITS.density.max}%. Thấp hơn thì chủ đề mờ; cao hơn là nhồi từ khoá, Google đánh giá thấp và người đọc thấy khó chịu.`,
    })
    const headingMatches = headings.map((hd) => matchKeyword(hd.text, kw))
    add({
      id: 'kw-in-heading', group: 'content', weight: 2,
      label: 'Từ khoá trong một tiêu đề phụ (H2/H3)',
      status: headingMatches.includes('exact') ? 'good' : headingMatches.includes('partial') || headings.length ? 'warn' : 'bad',
      hint: 'Tiêu đề phụ chứa từ khoá (hoặc biến thể) củng cố chủ đề và giúp lấy được đoạn trích nổi bật.',
    })
  }
  if (wordCount >= wl.warn) {
    const subs = headings.filter((hd) => hd.level >= 2).length
    add({
      id: 'has-headings', group: 'content', weight: 2,
      label: `Chia mục bằng tiêu đề phụ (${subs} mục)`,
      status: subs >= 2 ? 'good' : subs === 1 ? 'warn' : 'bad',
      hint: 'Nội dung dài phải có H2/H3 để người đọc quét được và Google hiểu cấu trúc. Dùng nút H2/H3 trên thanh công cụ soạn thảo.',
    })
  }
  const h1InContent = headings.filter((hd) => hd.level === 1).length
  if (h1InContent > 0) {
    add({
      id: 'no-h1-in-content', group: 'content', weight: 2,
      label: `Nội dung có ${h1InContent} thẻ H1 lồng thêm`,
      status: 'bad',
      hint: 'Mỗi trang chỉ nên có đúng một H1 (là tên sản phẩm/bài viết do web tự render). Trong phần soạn nội dung hãy dùng H2 trở xuống.',
    })
  }

  // ── Liên kết ───────────────────────────────────────────────────────────────
  add({
    id: 'internal-links', group: 'links', weight: 2,
    label: `Liên kết nội bộ (${stats.internalLinks})`,
    status: stats.internalLinks >= 1 ? 'good' : 'warn',
    hint: kind === 'product'
      ? 'Trỏ sang sản phẩm liên quan hoặc bài hướng dẫn để khách xem thêm và để Google bò sâu vào site.'
      : kind === 'category'
        ? 'Trỏ sang danh mục con hoặc bài viết liên quan. Dùng đường dẫn tương đối (`/danh-muc/…`) — link nội bộ mới không bị gắn nofollow.'
        : 'Bài viết nên trỏ về ít nhất một sản phẩm hoặc bài liên quan — đây là cách bài blog tạo ra đơn hàng.',
  })
  add({
    id: 'external-links', group: 'links', weight: 1,
    label: `Liên kết ra ngoài (${stats.externalLinks})`,
    status: kind === 'post' ? (stats.externalLinks >= 1 ? 'good' : 'warn') : 'good',
    hint: 'Dẫn nguồn ra trang uy tín làm nội dung đáng tin hơn. Hệ thống tự thêm rel="nofollow noopener" cho link ra ngoài khi lưu (link nội bộ thì không).',
  })

  // ── Hình ảnh ───────────────────────────────────────────────────────────────
  if (kind === 'product') {
    add({
      id: 'product-images', group: 'media', weight: 3,
      label: `Số ảnh sản phẩm (${attached.length})`,
      status: attached.length >= SEO_LIMITS.productImages ? 'good' : attached.length >= 1 ? 'warn' : 'bad',
      hint: `Nên có ≥ ${SEO_LIMITS.productImages} ảnh: toàn cảnh, cận cảnh chi tiết, ảnh dùng thật. Ảnh là yếu tố quyết định mua và là điều kiện của rich result sản phẩm.`,
    })
  } else {
    add({
      id: 'featured-image', group: 'media', weight: 3,
      label: kind === 'category' ? 'Ảnh đại diện danh mục' : 'Ảnh đại diện bài viết',
      status: attached.length >= 1 ? 'good' : 'bad',
      hint: kind === 'category'
        ? 'Danh mục không có ảnh thì thẻ danh mục ở trang chủ trống trơn, và chia sẻ link lên Facebook/Zalo chỉ hiện chữ.'
        : 'Bài không có ảnh đại diện thì khi chia sẻ lên Facebook/Zalo chỉ hiện chữ, tỉ lệ bấm rất thấp.',
    })
  }
  add({
    id: 'image-alts', group: 'media', weight: 2,
    label: stats.imagesMissingAlt ? `${stats.imagesMissingAlt} ảnh thiếu alt` : `Mọi ảnh đều có alt (${allImages.length})`,
    status: !allImages.length ? 'bad' : stats.imagesMissingAlt === 0 ? 'good' : 'bad',
    hint: 'Alt là cách duy nhất Google hiểu ảnh, và là đường vào từ Google Images. Cũng là yêu cầu tiếp cận cho người dùng đọc màn hình.',
  })
  if (hasKw && allImages.length) {
    add({
      id: 'kw-in-alt', group: 'media', weight: 1,
      label: 'Từ khoá trong alt của một ảnh',
      status: allImages.some((i) => matchKeyword(i.alt, kw) !== 'none') ? 'good' : 'warn',
      hint: 'Một alt chứa từ khoá là đủ — đừng nhồi từ khoá vào tất cả alt, hãy mô tả đúng từng ảnh.',
    })
  }
  add({
    id: 'og-image', group: 'media', weight: 2,
    label: 'Ảnh chia sẻ (Open Graph)',
    status: input.hasMetaImage ? 'good' : attached.length ? 'warn' : 'bad',
    hint: 'Ảnh OG 1200×630 hiện khi dán link lên Facebook/Zalo. Không đặt thì mạng xã hội lấy ảnh bất kỳ, thường bị cắt xấu.',
  })

  // ── Dễ đọc ─────────────────────────────────────────────────────────────────
  if (wordCount >= 60) {
    const longRatio = sentences.length ? longSentences / sentences.length : 0
    add({
      id: 'sentence-length', group: 'readability', weight: 1,
      label: `Câu dài (${longSentences}/${sentences.length}, trung bình ${avgSentenceWords} từ/câu)`,
      status: longRatio <= 0.25 ? 'good' : longRatio <= 0.4 ? 'warn' : 'bad',
      hint: `Câu quá ${SEO_LIMITS.longSentenceWords} từ khó đọc trên điện thoại. Tách thành câu ngắn, mỗi câu một ý.`,
    })
    add({
      id: 'paragraph-length', group: 'readability', weight: 1,
      label: longParagraphs ? `${longParagraphs} đoạn quá dài` : 'Các đoạn có độ dài hợp lý',
      status: longParagraphs === 0 ? 'good' : longParagraphs <= 2 ? 'warn' : 'bad',
      hint: `Đoạn quá ${SEO_LIMITS.longParagraphWords} từ trông như một khối chữ; người đọc bỏ qua. Xuống dòng thường xuyên hơn.`,
    })
  }

  // ── Kỹ thuật ───────────────────────────────────────────────────────────────
  add({
    id: 'indexable', group: 'technical', weight: 3,
    label: input.noindex ? 'Trang đang chặn Google (noindex)' : 'Cho phép Google lập chỉ mục',
    status: input.noindex ? 'bad' : 'good',
    hint: 'noindex nghĩa là trang sẽ không bao giờ xuất hiện trên Google. Chỉ dùng cho trang trùng lặp hoặc trang nội bộ.',
  })
  if (input.canonical) {
    add({
      id: 'canonical', group: 'technical', weight: 1,
      label: 'Canonical tự đặt',
      status: /^https?:\/\//i.test(input.canonical) ? 'warn' : 'bad',
      hint: 'Canonical trỏ sang URL khác nghĩa là "trang này là bản sao, hãy xếp hạng URL kia". Để trống là an toàn nhất, trừ khi bạn biết chắc mình đang gộp trùng lặp.',
    })
  }

  // ── Rich result ────────────────────────────────────────────────────────────
  /** Sản phẩm và danh mục dùng chung mục này — cả hai đều render schema `FAQPage`. */
  const addFaqCheck = () => add({
    id: 'faq', group: 'rich', weight: 1,
    label: `Câu hỏi thường gặp (${input.faqCount ?? 0})`,
    status: (input.faqCount ?? 0) >= 2 ? 'good' : 'warn',
    hint: 'Từ 2 câu hỏi trở lên sẽ sinh schema FAQ, chiếm thêm diện tích trên trang kết quả và trả lời sẵn thắc mắc trước khi khách hỏi Zalo.',
  })

  if (kind === 'product') {
    add({
      id: 'price-set', group: 'rich', weight: 3,
      label: 'Đã có giá',
      status: input.price != null && input.price > 0 ? 'good' : 'bad',
      hint: 'Không có giá thì Google không hiện được kết quả nổi bật dạng sản phẩm (giá, tình trạng hàng) — mất lợi thế lớn trên SERP.',
    })
    add({
      id: 'category-set', group: 'rich', weight: 2,
      label: 'Đã gán danh mục',
      status: input.hasCategory ? 'good' : 'warn',
      hint: 'Danh mục tạo breadcrumb trên Google và giúp khách lọc đúng nhóm sản phẩm.',
    })
    add({
      id: 'brand-set', group: 'rich', weight: 1,
      label: 'Có thương hiệu (brand)',
      status: input.brand ? 'good' : 'warn',
      hint: 'Schema `Product.brand` là trường Google khuyến nghị. Điền tên shop nếu là hàng tự làm.',
    })
    add({
      id: 'sku-set', group: 'rich', weight: 1,
      label: 'Có SKU',
      status: input.hasSku ? 'good' : 'warn',
      hint: 'SKU giúp đối chiếu tồn kho với Shopee và là trường khuyến nghị của schema sản phẩm.',
    })
    addFaqCheck()
    if (input.hasVideo) {
      add({ id: 'video', group: 'rich', weight: 1, label: 'Có video', status: 'good', hint: 'Video giữ khách ở lại trang lâu hơn và có thể lên tab Video của Google.' })
    }
  } else if (kind === 'category') {
    const products = input.productCount ?? 0
    add({
      id: 'products-in-category', group: 'rich', weight: 3,
      label: `Sản phẩm trong danh mục (${products})`,
      status: products >= 3 ? 'good' : products >= 1 ? 'warn' : 'bad',
      hint: 'Danh mục rỗng là trang mỏng điển hình: Google thấy một tiêu đề và không có gì khác. Gán sản phẩm vào, hoặc ẩn danh mục đi cho tới khi có hàng.',
    })
    /**
     * Chỉ thêm khi CÓ danh mục con, và khi đó luôn `good` — theo đúng khuôn mục `video`
     * của sản phẩm. Chấm `warn` khi không có là phạt mọi nhánh lá vì tội… là nhánh lá,
     * và khiến chúng không bao giờ đạt nổi 100 dù đã làm đúng hết.
     */
    if ((input.childCount ?? 0) >= 1) add({
      id: 'subcategories', group: 'rich', weight: 1,
      label: `Danh mục con (${input.childCount})`,
      status: 'good',
      hint: 'Danh mục con tạo liên kết nội bộ và giúp khách thu hẹp lựa chọn.',
    })
    addFaqCheck()
  } else {
    add({
      id: 'excerpt', group: 'rich', weight: 2,
      label: 'Có tóm tắt',
      status: input.hasExcerpt ? 'good' : 'warn',
      hint: 'Tóm tắt dùng cho danh sách bài và làm meta description dự phòng.',
    })
    add({
      id: 'author', group: 'rich', weight: 1,
      label: 'Có tác giả',
      status: input.hasAuthor ? 'good' : 'warn',
      hint: 'Schema `Article.author` là một tín hiệu E-E-A-T (kinh nghiệm, chuyên môn, uy tín).',
    })
    add({
      id: 'published-date', group: 'rich', weight: 2,
      label: 'Có ngày đăng',
      status: input.hasPublishedAt ? 'good' : 'warn',
      hint: 'Ngày đăng hiện trên SERP và cho Google biết nội dung còn mới.',
    })
    add({
      id: 'tags', group: 'rich', weight: 1,
      label: `Có tag (${input.tagCount ?? 0})`,
      status: (input.tagCount ?? 0) >= 1 ? 'good' : 'warn',
      hint: 'Tag gom các bài cùng chủ đề, tạo liên kết nội bộ tự nhiên.',
    })
  }

  // ── Điểm ───────────────────────────────────────────────────────────────────
  const totalWeight = checks.reduce((s, c) => s + c.weight, 0)
  const earned = checks.reduce((s, c) => s + c.weight * (c.status === 'good' ? 1 : c.status === 'warn' ? 0.5 : 0), 0)
  const score = totalWeight ? Math.round((earned / totalWeight) * 100) : 0
  const rating: SeoResult['rating'] = score >= 80 ? 'good' : score >= 55 ? 'ok' : 'bad'

  const order: Record<CheckStatus, number> = { bad: 0, warn: 1, good: 2 }
  const todos = checks
    .filter((c) => c.status !== 'good')
    .sort((a, b) => order[a.status] - order[b.status] || b.weight - a.weight)

  return { score, rating, checks, todos, stats }
}

// ─────────────────────────────────────────────────────────────────────────────
// Mẫu tiêu đề
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Điền mẫu tiêu đề toàn site, vd `%title% | %sitename%`.
 * Biến trống bị bỏ cùng dấu phân cách phía trước để không ra "Tên sản phẩm | ".
 */
export function renderTitleTemplate(
  template: string,
  vars: { title?: string; sitename?: string; category?: string; separator?: string },
): string {
  const sep = vars.separator ?? '|'
  const out = (template || '%title%')
    .replace(/%title%/g, vars.title ?? '')
    .replace(/%sitename%/g, vars.sitename ?? '')
    .replace(/%category%/g, vars.category ?? '')
    .replace(/%sep%/g, sep)
  return out
    .split(new RegExp(`\\s*\\${sep}\\s*`))
    .map((p) => p.trim())
    .filter(Boolean)
    .join(` ${sep} `)
}
